import * as Sentry from '@sentry/react';

const isAbortError = (error) =>
  error?.error?.name === 'AbortError' ||
  error?.name === 'AbortError' ||
  error?.message === 'Aborted';

/**
 * Centralized error reporting utility.
 *
 * @param {Error|string|unknown} error - The error to capture
 * @param {Object} [context] - Optional context
 * @param {string} [context.message] - Human-readable message shown as extra context
 * @param {string} [context.severity] - Sentry severity level ('fatal'|'error'|'warning'|'info'|'debug')
 * @param {Object} [context.tags] - Key-value tags for filtering in Sentry dashboard
 * @param {Object} [context.extra] - Additional data to attach to the event
 * @param {string} [context.component] - Component/service name (added as a tag)
 * @param {boolean} [context.skipConsole] - If true, skip console.error output
 */
export const captureError = (error, context = {}) => {
  if (isAbortError(error)) return;

  const {
    message,
    severity = 'error',
    tags = {},
    extra = {},
    component,
    skipConsole = false,
  } = context;

  if (!skipConsole) {
    console.error(message || 'Captured error', error);
  }

  if (!Sentry.getClient()) return;

  Sentry.withScope((scope) => {
    scope.setLevel(severity);

    if (component) {
      scope.setTag('component', component);
    }

    Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value));
    Object.entries(extra).forEach(([key, value]) => scope.setExtra(key, value));

    if (message) {
      scope.setExtra('userMessage', message);
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
    } else if (typeof error === 'string') {
      Sentry.captureException(new Error(error));
    } else {
      // RTK Query errors are plain objects — wrap them
      const wrappedError = new Error(message || 'Unknown error');
      try {
        scope.setExtra('rawError', JSON.stringify(error)?.substring(0, 2000));
      } catch {
        scope.setExtra('rawError', String(error));
      }
      Sentry.captureException(wrappedError);
    }
  });
};
